<template>
  <!-- prettier-ignore -->
  <div class="basket">
    <ovp-a-b-config />

    <ovp-icon
      v-if="isLoading"
      class="fa-spin"
      art="light"
      name="spinner-third"
    />

    <button
      v-else
      id="basketWidgetToggle"
      class="basket-toggle"
      plain
      @click="toggle">
      <ovp-icon
        art="light"
        class="basket-toggle__cart"
        name="cart-shopping"
      />

      <ovp-icon
        art="light"
        class="basket-toggle__state"
        name="angle-down"
        :flip="isOpen ? 'vertical' : 'horizontal'"
      />
      <div class="basket-toggle__indicator">{{ products }}</div>
    </button>

    <ovp-basket-widget-overlay
      :is-compact="compact"
      :is-manually-opened="manuallyOpened"
      :is-open="isOpen"
      :is-www="isWww"
      :message="message"
      :persons="persons"
      :readonly="readonly"
      :total="total"
      @toggle-compact="toggleCompact"
      @action-click="gotoBasket"
      @close="toggle"
    />

    <ovp-modal
      v-slot="{ modalProps }"
      v-model="showBasketScreenSaveModal"
      x-only
      @close="saveModalSource = ''"
    >
      <basket-save-modal
        :source="saveModalSource"
        @saved="modalProps.submit"
        @canceled="modalProps.cancel"
      />
    </ovp-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import OvpABConfig from '@/components/Container/ABConfig'
import OvpBasketWidgetOverlay from '@/components/BasketWidget/Container/Overlay'
import BasketSaveModal from '@/components/BasketWidget/Modals/BasketSaveModal'
import OvpModal from '@/components/Modal/Modal'

import { STATE_LOADING, STATE_READY } from '@/config/constants'
import {
  EVENT_BASKET_OPEN,
  EVENT_BASKET_CLOSE,
  EVENT_BASKET_WIDGET_CLOSE,
  EVENT_BASKET_OPEN_SCREEN_SAVE_MODAL,
  EVENT_PRODUCT_ADDED,
  EVENT_PRODUCT_CHANGED,
  EVENT_PRODUCT_REMOVED,
  SOURCE_BASKET_WIDGET,
  SOURCE_BASKET,
  EVENT_DOTCOM_SURVEY_WEBSITE,
  SOURCE_AIDE_BASKET,
} from '@/config/events'
import StickyBasket from '@/directives/StickyBasket'
import { BasketHelper, PersonHelper, StatusHelper } from '@/helpers'
import HeightTransition from '@/mixins/HeightTransition.js'

export default {
  name: 'Container',

  mixins: [HeightTransition],

  directives: { StickyBasket },

  components: {
    OvpABConfig,
    BasketSaveModal,
    OvpBasketWidgetOverlay,
    OvpModal,
  },

  props: {
    options: {
      type: Object,
      required: true,
    },

    state: {
      type: String,
      default: STATE_READY,
    },

    isWww: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      modalName: 'OvpModal',
      open: false,
      manuallyOpened: false,
      message: null,
      compact: false,
      showBasketScreenSaveModal: false,
      saveModalSource: '',
    }
  },

  mounted() {
    this.$EventBus.$on(EVENT_PRODUCT_ADDED, event => {
      const { personId, productId } = event
      if (personId && productId) {
        const firstName = PersonHelper.getFirstName(personId, this.$t.bind(this))
        const product = this.$t(`content.products.${productId}.name`)
        this.message = this.$t('basket.message', { product, firstName })
      }
    })

    this.$EventBus.$on(EVENT_PRODUCT_REMOVED, event => {
      const isNotLastProductAndDeletedWithinWidget = this.hasProducts && event?.source === SOURCE_BASKET_WIDGET
      this.compact = !isNotLastProductAndDeletedWithinWidget
      this.open = !this.isBasketScreen
      this.manuallyOpened = isNotLastProductAndDeletedWithinWidget
      this.message = null
    })

    this.$EventBus.$on([EVENT_PRODUCT_ADDED, EVENT_PRODUCT_CHANGED], _event => {
      if (this.hasProducts) {
        this.compact = true
        this.open = !this.isBasketScreen
        this.manuallyOpened = false
      }
    })

    this.$EventBus.$on(EVENT_BASKET_OPEN, _event => {
      this.compact = false
      this.open = true
      this.manuallyOpened = false
    })

    this.$EventBus.$on(EVENT_BASKET_CLOSE, () => {
      this.open = false
    })

    this.$EventBus.$on(EVENT_BASKET_OPEN_SCREEN_SAVE_MODAL, data => {
      this.saveModalSource = data.source

      if (data.source === SOURCE_BASKET) {
        this.showBasketScreenSaveModal = true
      } else if (data.source === SOURCE_AIDE_BASKET) {
        //  do nothing, handled in Aide
      } else {
        throw new Error(`EVENT_BASKET_OPEN_SCREEN_SAVE_MODAL unknown source ${JSON.stringify(data)}`)
      }
    })
  },

  watch: {
    total: function (oldPrice, newPrice) {
      if (oldPrice !== newPrice) {
        this.open = true
        this.compact = true
      }
    },
    open: function (value) {
      if (!value && this.hasProducts) {
        this.$EventBus.$emit(EVENT_BASKET_WIDGET_CLOSE)
      }
    },
  },

  computed: {
    ...mapState(['basket']),
    ...mapGetters('basket', ['getProduct']),

    isLoading() {
      return this.state === STATE_LOADING
    },

    isOpen() {
      return this.open
    },

    isBasketScreen() {
      return window.ovpRouter && this.$route?.name === 'basket'
    },

    hasProducts() {
      return StatusHelper.basketHasProducts(this.persons)
    },

    persons() {
      return PersonHelper.getPersonsWithTotal()
    },

    products() {
      return this.persons.reduce((acc, value) => {
        acc += value.products.length
        return acc
      }, 0)
    },

    readonly() {
      const isStaticBasket = BasketHelper.isStaticBasket(this.basket)
      return this.options.readonly || isStaticBasket
    },

    total() {
      return BasketHelper.getTotal(this.persons)
    },
  },

  methods: {
    ...mapActions('basket', ['setBasket']),

    gotoBasket() {
      const $router = window.ovpRouter
      if ($router) {
        if (this.isBasketScreen) {
          this.open = false
          return
        }
        $router.push('/basket')
      } else {
        let url = this.options.configuratorUrl
        if (this.hasProducts) {
          url += '#/basket'
        }
        window.location.href = url
      }
    },

    toggle(_$event) {
      this.open = this.manuallyOpened = !this.open

      if (!this.open) {
        this.message = null

        if (this.isWww) {
          this.$EventBus.$emit(EVENT_DOTCOM_SURVEY_WEBSITE)
        }
      }
    },

    toggleCompact() {
      this.compact = !this.compact
      this.manuallyOpened = true
    },
  },
}
</script>

<style name="mobile" scoped>
.basket {
  position: relative;
  font-family: Open Sans, sans-serif;

  &-toggle {
    background: none;
    border: none;
    outline: none;
    cursor: pointer;

    display: flex;
    flex-direction: row;

    position: relative;

    .icon {
      font-size: 20px;
      color: var(--c-black);
    }

    &__cart {
    }

    &__state {
      margin-left: 10px;
    }

    &__indicator {
      position: absolute;
      left: 20px;
      border-radius: 6px;
      background-color: var(--c-original-green);
      color: var(--c-white);
      padding: 2px 5px;
      opacity: 0.8;
      font-size: 10px;
      top: -5px;
    }
  }
}
</style>
